// src/components/session/LoginControl.tsx
import React, { useState } from 'react';
import { Button, Form, Container, Col, Alert, Card, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useLogin from '../../hooks/useLogin';
import { UserProfile } from '../../interfaces';
import logo from 'assets/logo.png';
import logo_full from 'assets/logo_full.png';

interface LoginControlProps {
  onLogin: (data: UserProfile) => void;
}

const LoginControl: React.FC<LoginControlProps> = ({ onLogin }) => {
  const { t } = useTranslation();
  const { login, loginWithProvider, error, loading } = useLogin(); // ⬅️ use provider method from hook
  const [formData, setFormData] = useState({
    id: 0,
    login: '',
    password: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = await login(formData);
    if (data) onLogin(data);
  };

  // Use the hook to start the GitLab OAuth flow
  const handleGitLabLogin = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // Optionally pass a custom return URL: loginWithProvider('gitlab', `${window.location.origin}/auth/gitlab`)
    loginWithProvider('gitlab');
  };

  return (
    <Container className="position-absolute top-25 start-50 translate-middle-x mt-5">
      <Card className='d-flex align-items-center border-0 bg-transparent'>
        <Image className="m-3" src={logo} alt={logo} height={200} />
        <h1 className="text-secondary fw-semibold py-3 mb-3">{t('login.signIn')}
          <Image className='ms-2 mb-1' src={logo_full} alt="Logo_full" height={30} />
        </h1>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form className='w-50' onSubmit={handleSubmit}>
          <Form.Group className="mb-4 fs-4 fw-medium text-secondary" controlId="formBasiclogin">
            <Form.Label className="position-relative top-25 start-50 translate-middle-x">{t('userProfile.userName')}</Form.Label>
            <Form.Control
              type="text"
              name="login"
              placeholder={t('login.enterlogin')}
              value={formData.login}
              onChange={handleChange}
              required
              className="form__field text-secondary fw-medium bg-white px-3 py-2"
            />
          </Form.Group>
          <Form.Group className="mb-4 fs-4 fw-medium text-secondary" controlId="formBasicPassword">
            <Form.Label className="position-relative top-25 start-50 translate-middle-x">{t('login.password')}</Form.Label>
            <Form.Control
              type="password"
              name="password"
              placeholder={t('login.enterPassword')}
              value={formData.password}
              onChange={handleChange}
              required
              className="form__field text-secondary fw-medium fs-5 bg-white px-3 py-2" 
            />
          </Form.Group>
          <Col className="d-grid gap-2">
            <Button className='border border-2 rounded-3 text-white fw-medium fs-5 px-3 py-2' variant="secondary" type="submit">
              {t('login.login')}
            </Button>

            <Col className="text-center text-muted">{t('login.or', 'or')}</Col>

            <Button
              variant="outline-secondary"
              type="button"
              onClick={handleGitLabLogin}
              className="border border-2 rounded-3 px-3 py-2"
              aria-label={t('login.signInWithGitLab', 'Sign in with GitLab')}
              disabled={loading}
            >
              {t('login.signInWithGitLab', 'Sign in with GitLab')}
            </Button>
          </Col>
        </Form>
      </Card>
    </Container>
  );
};

export default LoginControl;
